/**
 * API Utilities
 * Centralized API configuration and helper functions
 * Production-ready with proper error handling and fallback
 */

// Import API configuration
import { API_CONFIG } from '../config/api-config';

// Get or create user session
export const getUserSession = () => {
  let session = localStorage.getItem('userSession');
  if (!session) {
    session = 'user_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('userSession', session);
  }
  return session;
};

// Request cache to avoid redundant API calls
const requestCache = new Map();
const CACHE_DURATION = 30000; // 30 seconds cache

// Improved API request helper with caching and faster timeout
export const apiRequest = async (endpoint, options = {}) => {
  // Check cache for GET requests
  const cacheKey = `${endpoint}_${JSON.stringify(options)}`;
  if (options.method !== 'POST' && options.method !== 'PUT' && options.method !== 'DELETE') {
    const cached = requestCache.get(cacheKey);
    if (cached && Date.now() - cached.time < CACHE_DURATION) {
      return cached.data;
    }
  }

  const url = `${API_CONFIG.BASE_URL}${endpoint}`;
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers
    },
    timeout: 3000, // Reduced to 3 seconds
    ...options
  };

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), config.timeout);
    
    const response = await fetch(url, {
      ...config,
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Cache successful GET requests
    if (options.method !== 'POST' && options.method !== 'PUT' && options.method !== 'DELETE') {
      requestCache.set(cacheKey, { data, time: Date.now() });
    }
    
    return data;
  } catch (error) {
    console.error(`API Request Error for ${endpoint}:`, error);
    throw error;
  }
};

// Clear cache when needed
export const clearCache = () => {
  requestCache.clear();
};

// Topics API - Production ready with server persistence
export const topicsAPI = {
  // Get all topics from server
  getAll: async () => {
    try {
      const data = await apiRequest(API_CONFIG.ENDPOINTS.TOPICS);
      // If server returns empty but we have local, use local as backup
      if (Array.isArray(data)) {
        return data;
      }
      throw new Error('Invalid response format');
    } catch (error) {
      console.error('Failed to fetch topics from server:', error);
      // Return empty array to force fresh load from server
      return [];
    }
  },
  
  getOne: async (id) => {
    try {
      const data = await apiRequest(`${API_CONFIG.ENDPOINTS.TOPICS}?id=${id}`);
      return data;
    } catch (error) {
      console.error('Failed to fetch topic:', error);
      throw error;
    }
  },
  
  create: async (topicData) => {
    try {
      const data = await apiRequest(API_CONFIG.ENDPOINTS.TOPICS, {
        method: 'POST',
        body: JSON.stringify(topicData)
      });
      return data;
    } catch (error) {
      console.error('Failed to create topic:', error);
      throw error;
    }
  },
  
  update: async (topicData) => {
    try {
      const data = await apiRequest(API_CONFIG.ENDPOINTS.TOPICS, {
        method: 'PUT',
        body: JSON.stringify(topicData)
      });
      return data;
    } catch (error) {
      console.error('Failed to update topic:', error);
      throw error;
    }
  },
  
  delete: async (id) => {
    try {
      const data = await apiRequest(API_CONFIG.ENDPOINTS.TOPICS, {
        method: 'DELETE',
        body: JSON.stringify({ id })
      });
      return data;
    } catch (error) {
      console.error('Failed to delete topic:', error);
      throw error;
    }
  }
};

// Bookmarks API
export const bookmarksAPI = {
  get: async (session = getUserSession()) => {
    try {
      return await apiRequest(`${API_CONFIG.ENDPOINTS.BOOKMARKS}?session=${session}`);
    } catch (error) {
      console.error('Failed to fetch bookmarks:', error);
      return [];
    }
  },
  
  add: async (topicId, session = getUserSession()) => {
    try {
      return await apiRequest(API_CONFIG.ENDPOINTS.BOOKMARKS, {
        method: 'POST',
        body: JSON.stringify({ session, topicId })
      });
    } catch (error) {
      console.error('Failed to add bookmark:', error);
      throw error;
    }
  },
  
  remove: async (topicId, session = getUserSession()) => {
    try {
      return await apiRequest(API_CONFIG.ENDPOINTS.BOOKMARKS, {
        method: 'DELETE',
        body: JSON.stringify({ session, topicId })
      });
    } catch (error) {
      console.error('Failed to remove bookmark:', error);
      throw error;
    }
  }
};

// Quiz API
export const quizAPI = {
  getResults: async (session = getUserSession()) => {
    try {
      return await apiRequest(`${API_CONFIG.ENDPOINTS.QUIZ}?session=${session}`);
    } catch (error) {
      console.error('Failed to fetch quiz results:', error);
      return [];
    }
  },
  
  getTopicResult: async (topicId, session = getUserSession()) => {
    try {
      return await apiRequest(`${API_CONFIG.ENDPOINTS.QUIZ}?session=${session}&topicId=${topicId}`);
    } catch (error) {
      console.error('Failed to fetch topic quiz result:', error);
      return null;
    }
  },
  
  saveResult: async (topicId, score, total, answers, session = getUserSession()) => {
    try {
      return await apiRequest(API_CONFIG.ENDPOINTS.QUIZ, {
        method: 'POST',
        body: JSON.stringify({ session, topicId, score, total, answers })
      });
    } catch (error) {
      console.error('Failed to save quiz result:', error);
      throw error;
    }
  }
};

// Progress API
export const progressAPI = {
  get: async (session = getUserSession()) => {
    try {
      return await apiRequest(`${API_CONFIG.ENDPOINTS.PROGRESS}?session=${session}`);
    } catch (error) {
      console.error('Failed to fetch progress:', error);
      return {};
    }
  },
  
  update: async (topicId, completed = true, session = getUserSession()) => {
    try {
      return await apiRequest(API_CONFIG.ENDPOINTS.PROGRESS, {
        method: 'POST',
        body: JSON.stringify({ session, topicId, completed })
      });
    } catch (error) {
      console.error('Failed to update progress:', error);
      throw error;
    }
  }
};

// Admin API
export const adminAPI = {
  login: async (username, password) => {
    try {
      return await apiRequest(`${API_CONFIG.ENDPOINTS.ADMIN}?action=login`, {
        method: 'POST',
        body: JSON.stringify({ username, password })
      });
    } catch (error) {
      console.error('Failed to login:', error);
      throw error;
    }
  },
  
  logout: async (token) => {
    try {
      return await apiRequest(`${API_CONFIG.ENDPOINTS.ADMIN}?action=logout`, {
        method: 'POST',
        body: JSON.stringify({ token })
      });
    } catch (error) {
      console.error('Failed to logout:', error);
      throw error;
    }
  },
  
  getAdSettings: async () => {
    try {
      return await apiRequest(`${API_CONFIG.ENDPOINTS.ADMIN}?action=ad-settings`);
    } catch (error) {
      console.error('Failed to fetch ad settings:', error);
      return {};
    }
  },
  
  updateAdSettings: async (settings) => {
    try {
      return await apiRequest(`${API_CONFIG.ENDPOINTS.ADMIN}?action=ad-settings`, {
        method: 'PUT',
        body: JSON.stringify(settings)
      });
    } catch (error) {
      console.error('Failed to update ad settings:', error);
      throw error;
    }
  }
};

// Local storage fallback - ONLY for user-specific data
export const fallbackStorage = {
  // Topics should NEVER use fallback - always use server
  getTopics: () => {
    const topics = localStorage.getItem('topics_backup');
    return topics ? JSON.parse(topics) : [];
  },
  
  setTopics: (topics) => {
    // Store as backup only
    localStorage.setItem('topics_backup', JSON.stringify(topics));
  },
  
  // Bookmarks are user-specific, use fallback
  getBookmarks: () => {
    const bookmarks = localStorage.getItem('bookmarks');
    return bookmarks ? JSON.parse(bookmarks) : [];
  },
  
  setBookmarks: (bookmarks) => {
    localStorage.setItem('bookmarks', JSON.stringify(bookmarks));
  },
  
  // Quiz results are user-specific, use fallback
  getQuizResults: () => {
    const results = localStorage.getItem('quizResults');
    return results ? JSON.parse(results) : {};
  },
  
  setQuizResults: (results) => {
    localStorage.setItem('quizResults', JSON.stringify(results));
  },
  
  // Progress is user-specific, use fallback
  getProgress: () => {
    const progress = localStorage.getItem('progress');
    return progress ? JSON.parse(progress) : {};
  },
  
  setProgress: (progress) => {
    localStorage.setItem('progress', JSON.stringify(progress));
  },
  
  // Ad settings are shared, use fallback
  getAdSettings: () => {
    const settings = localStorage.getItem('adSettings');
    return settings ? JSON.parse(settings) : {
      topBanner: true,
      bottomBanner: true,
      sidebar: true,
      contentAd: true
    };
  },
  
  setAdSettings: (settings) => {
    localStorage.setItem('adSettings', JSON.stringify(settings));
  }
};